import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from './Modal';
import GradedCanvas from './GradedCanvas';
import SplitPreview from './SplitPreview';
import { requestMaster } from '../data/firestore';
import { DEFAULT_GRADE, isIdentityGrade } from '../utils/grade';
import { masterSegments, masterDuration, clipIndexAtMaster, masterCutSig } from '../utils/movie';
import { jobEtaLabel, estimateMasterSec, fmtDuration, useNow } from '../utils/eta';
import { httpsify } from '../utils/url';

// Quick before/after of a movie's MOVIE-WIDE grade, previewed against the flat
// master MP4. On a RAW master (no looks baked) we apply each clip's own look
// live beneath both panes — so the right pane = clip look + movie grade and the
// left = clip looks with the movie grade OFF, matching the published output.
export default function QuickCompareModal({ movie, onClose }) {
  const videoRef = useRef(null);
  const [videoEl, setVideoEl] = useState(null);
  const [err, setErr] = useState('');
  const [segIndex, setSegIndex] = useState(0);

  const master = movie?.master || {};
  const masterUrl = master.status === 'ready' ? httpsify(master.url) : '';
  const masterRaw = master.raw === true;
  const grade = movie?.grade || DEFAULT_GRADE;
  const composing = master.status === 'composing' || master.status === 'queued';
  const masterEtaSec = useMemo(() => estimateMasterSec(masterDuration(movie?.clips || [])), [movie]);
  const now = useNow(composing);

  const segs = useMemo(() => masterSegments(movie?.clips || []), [movie]);
  const segsRef = useRef(segs);
  segsRef.current = segs;

  // The active clip's own saved look (raw masters only) — applied as the base of
  // both panes so the comparison isolates just the movie-wide grade.
  const clipLook = useMemo(() => {
    if (!masterRaw) return null;
    const clip = segs[segIndex]?.clip || segs[0]?.clip || null;
    const cg = clip && clip.useGrade !== false ? clip.grade : null;
    return cg && !isIdentityGrade(cg) ? cg : null;
  }, [masterRaw, segs, segIndex]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const onReady = () => setVideoEl(v);
    v.addEventListener('loadeddata', onReady);
    if (v.readyState >= 2) onReady();
    return () => v.removeEventListener('loadeddata', onReady);
  }, [masterUrl]);

  // Track which clip is under the playhead so the live per-clip look follows
  // playback (only needed when previewing a raw master).
  useEffect(() => {
    if (!videoEl || !masterRaw) return undefined;
    let raf;
    let last = 0;
    const loop = (ts) => {
      if (ts - last > 120) {
        last = ts;
        const i = clipIndexAtMaster(segsRef.current, videoEl.currentTime || 0);
        if (i >= 0) setSegIndex((p) => (p === i ? p : i));
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [videoEl, masterRaw]);

  return (
    <Modal title={`Compare · ${movie.title}`} onClose={onClose} maxWidth={1100}>
      {!masterUrl ? (
        composing ? (
          <div>
            <div className="cs-help">{jobEtaLabel(master, { verb: 'Composing preview master', estSec: masterEtaSec, nowMs: now })}</div>
            <div className="cs-progress" style={{ marginTop: 8 }}><div style={{ width: `${master.progress || 5}%` }} /></div>
          </div>
        ) : (
          <div>
            <div className="cs-empty" style={{ marginBottom: 10 }}>
              {master.status === 'error'
                ? `Master failed: ${master.message || 'unknown error'}`
                : 'Build a flat preview master to compare the grade against your cut.'}
            </div>
            <button
              className="cs-btn cs-btn-primary"
              onClick={() => requestMaster(movie.id, masterCutSig(movie.clips))}
              title={`Compose the flat preview master (~${fmtDuration(masterEtaSec)})`}
            >
              Build preview master <span style={{ opacity: 0.7 }}>· ~{fmtDuration(masterEtaSec)}</span>
            </button>
          </div>
        )
      ) : (
        <>
          <video
            ref={videoRef}
            src={masterUrl}
            crossOrigin="anonymous"
            muted
            loop
            autoPlay
            playsInline
            style={{ display: 'none' }}
            onError={() => setErr('Could not load preview master.')}
          />
          {err ? (
            <div className="cs-empty">{err}</div>
          ) : videoEl ? (
            <SplitPreview panes={[{ label: masterRaw ? 'Movie grade off' : 'Ungraded master', color: '#888' }, { label: masterRaw ? 'Movie grade on' : 'Graded', color: 'var(--surf)' }]}>
              <GradedCanvas source={videoEl} grade={clipLook || DEFAULT_GRADE} onError={setErr} maxWidth={900} />
              <GradedCanvas source={videoEl} grade={grade} baseGrade={clipLook || undefined} onError={setErr} maxWidth={900} />
            </SplitPreview>
          ) : (
            <div className="cs-help" style={{ padding: 40 }}>Loading preview…</div>
          )}
          <div className="cs-help" style={{ marginTop: 10 }}>
            Compares the rendered master cut. Open Color to edit the grade.
          </div>
        </>
      )}
    </Modal>
  );
}
